import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Input from "components/DesignSystem/Input/Input";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  CUSTOM_FIELD_TYPE_AMOUNT_INPUT,
  CUSTOM_FIELD_TYPE_CHECKBOX,
  CUSTOM_FIELD_TYPE_COMBOBOX,
  CUSTOM_FIELD_TYPE_DATE_PICKER,
  CUSTOM_FIELD_TYPE_INPUT,
  CUSTOM_FIELD_TYPE_MONTH_PICKER,
  CUSTOM_FIELD_TYPE_PHONE_INPUT,
  CUSTOM_FIELD_TYPE_RADIO,
  CUSTOM_FIELD_TYPE_STRING,
  CUSTOM_FIELD_TYPE_YEAR_PICKER,
} from "constants/aiQuestionCardFields";
import {
  DISPLAY_DATE,
  MONTH_FORMAT,
  YEAR_FORMAT,
  YYYY_MM_DD,
} from "constants/date";
import dayjs from "dayjs";
import { Field, FieldProps, useFormikContext } from "formik";
import { ReactNode } from "react";
import { CustomField } from "./customField.types";
import { getSelectedComboboxOption } from "./customFieldSelector.utils";

type SimpleFieldProps = {
  field: CustomField;
  name: string;
  disabled?: boolean;
};

const EMPTY_SELECTED: string[] = [];

const FieldLabelWithTooltip = ({
  label,
  tooltip,
  required,
}: {
  label: string;
  tooltip?: string;
  required: boolean;
}) => (
  <div className="t-flex t-items-center">
    <Label required={required}>{label}</Label>
    {tooltip && (
      <div className="t-ml-1 t-pb-1.5">
        <ToolTip text={tooltip} side="right">
          <span>
            <Info />
          </span>
        </ToolTip>
      </div>
    )}
  </div>
);

const InputField = ({ field, name, disabled }: SimpleFieldProps) => (
  <Input
    withForm
    name={name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.is_required}
    infoText={field.tooltip}
    disabled={disabled}
  />
);

const PhoneInputField = ({ field, name, disabled }: SimpleFieldProps) => (
  <MobileInput
    name={name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.is_required}
    tooltipText={field.tooltip}
    disabled={disabled}
    block
  />
);

const AmountInputField = ({ field, name, disabled }: SimpleFieldProps) => (
  <PriceInput
    name={name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.is_required}
    tooltipText={field.tooltip}
    disabled={disabled}
  />
);

const DATE_PICKER_CONFIG = {
  [CUSTOM_FIELD_TYPE_DATE_PICKER]: {
    dateFormat: undefined,
    saveFormat: YYYY_MM_DD,
    pickerProps: {},
  },
  [CUSTOM_FIELD_TYPE_MONTH_PICKER]: {
    dateFormat: MONTH_FORMAT,
    saveFormat: "MMM-YYYY",
    pickerProps: { showMonthYearPicker: true },
  },
  [CUSTOM_FIELD_TYPE_YEAR_PICKER]: {
    dateFormat: YEAR_FORMAT,
    saveFormat: "YYYY",
    pickerProps: { showYearPicker: true },
  },
} as const;

const DatePickerField = ({
  field,
  name,
  pickerType,
  disabled,
}: SimpleFieldProps & {
  pickerType:
    | typeof CUSTOM_FIELD_TYPE_DATE_PICKER
    | typeof CUSTOM_FIELD_TYPE_MONTH_PICKER
    | typeof CUSTOM_FIELD_TYPE_YEAR_PICKER;
}) => {
  const { setFieldValue } = useFormikContext();
  const { dateFormat, saveFormat, pickerProps } =
    DATE_PICKER_CONFIG[pickerType];

  const handleDateChange = (date: Date) => {
    setFieldValue(name, date ? dayjs(date).format(saveFormat) : "");
  };

  return (
    <Field name={name}>
      {({ field: formikField }: FieldProps) => (
        <DateInput
          {...formikField}
          {...pickerProps}
          name={name}
          label={field.label}
          placeholder={field.placeholder || DISPLAY_DATE}
          required={field.is_required}
          tooltipText={field.tooltip}
          dateFormat={dateFormat}
          saveFormat={saveFormat}
          onDateChange={handleDateChange}
          portalId={name}
          disabled={disabled}
        />
      )}
    </Field>
  );
};

const ComboboxField = ({ field, name, disabled }: SimpleFieldProps) => {
  const { values } = useFormikContext<Record<string, string | string[]>>();
  const options: OptionData[] = (field.options || []).map((option) => ({
    label: option.label,
    value: option.value,
  }));
  const selectedOption = getSelectedComboboxOption(
    options,
    values[name],
    field.is_multi_select
  );

  return (
    <Combobox
      withForm
      name={name}
      label={field.label}
      placeholder={field.placeholder}
      required={field.is_required}
      tooltipText={field.tooltip}
      options={options}
      value={selectedOption}
      isMulti={field.is_multi_select}
      isDisabled={disabled}
      menuPortalTarget={document.body}
    />
  );
};

const RadioField = ({ field, name, disabled }: SimpleFieldProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, string>>();

  const handleChange = (value: string) => setFieldValue(name, value);

  return (
    <div>
      <FieldLabelWithTooltip
        label={field.label}
        tooltip={field.tooltip}
        required={field.is_required}
      />
      <Radio.Root
        value={values[name] || ""}
        onValueChange={handleChange}
        disabled={disabled}
        className="t-flex t-flex-col t-gap-2"
      >
        <Radio.Content>
          {field.options?.map((option) => (
            <Radio.Item key={option.value} value={option.value}>
              {option.label}
            </Radio.Item>
          ))}
        </Radio.Content>
      </Radio.Root>
    </div>
  );
};

const CheckboxGroupField = ({ field, name, disabled }: SimpleFieldProps) => {
  const { values, setFieldValue } =
    useFormikContext<Record<string, string[]>>();
  const selected = values[name] || EMPTY_SELECTED;

  const handleToggle = (optionValue: string, checked: boolean) => {
    const nextSelected = checked
      ? [...selected, optionValue]
      : selected.filter((value) => value !== optionValue);
    setFieldValue(name, nextSelected);
  };

  return (
    <div>
      <FieldLabelWithTooltip
        label={field.label}
        tooltip={field.tooltip}
        required={field.is_required}
      />
      <div className="t-flex t-flex-col t-gap-2">
        {field.options?.map((option) => (
          <Checkbox
            key={option.value}
            label={option.label}
            checked={selected.includes(option.value)}
            disabled={disabled}
            onChange={(e) => handleToggle(option.value, e.target.checked)}
          />
        ))}
      </div>
    </div>
  );
};

export const isSimpleFieldType = (type: CustomField["type"]): boolean =>
  [
    CUSTOM_FIELD_TYPE_INPUT,
    CUSTOM_FIELD_TYPE_STRING,
    CUSTOM_FIELD_TYPE_PHONE_INPUT,
    CUSTOM_FIELD_TYPE_AMOUNT_INPUT,
    CUSTOM_FIELD_TYPE_DATE_PICKER,
    CUSTOM_FIELD_TYPE_MONTH_PICKER,
    CUSTOM_FIELD_TYPE_YEAR_PICKER,
    CUSTOM_FIELD_TYPE_COMBOBOX,
    CUSTOM_FIELD_TYPE_RADIO,
    CUSTOM_FIELD_TYPE_CHECKBOX,
  ].includes(type);

export const renderSimpleField = ({
  field,
  name,
  disabled,
}: SimpleFieldProps): ReactNode => {
  switch (field.type) {
    case CUSTOM_FIELD_TYPE_INPUT:
    case CUSTOM_FIELD_TYPE_STRING:
      return <InputField field={field} name={name} disabled={disabled} />;
    case CUSTOM_FIELD_TYPE_PHONE_INPUT:
      return <PhoneInputField field={field} name={name} disabled={disabled} />;
    case CUSTOM_FIELD_TYPE_AMOUNT_INPUT:
      return <AmountInputField field={field} name={name} disabled={disabled} />;
    case CUSTOM_FIELD_TYPE_DATE_PICKER:
    case CUSTOM_FIELD_TYPE_MONTH_PICKER:
    case CUSTOM_FIELD_TYPE_YEAR_PICKER:
      return (
        <DatePickerField
          field={field}
          name={name}
          pickerType={field.type}
          disabled={disabled}
        />
      );
    case CUSTOM_FIELD_TYPE_COMBOBOX:
      return <ComboboxField field={field} name={name} disabled={disabled} />;
    case CUSTOM_FIELD_TYPE_RADIO:
      return <RadioField field={field} name={name} disabled={disabled} />;
    case CUSTOM_FIELD_TYPE_CHECKBOX:
      return (
        <CheckboxGroupField field={field} name={name} disabled={disabled} />
      );
    default:
      return null;
  }
};
